import {BaseCurrencyCode, DoubleString, Percent, Resource} from '@raison/types';
import BigNumber from 'bignumber.js';
import {CurrencyService} from '~/redux/assets/CurrencyService';
import {selectCurrencyServiceResource, selectTokenAccountsResource} from '~/redux/assets/assetsResource';
import {selectBalancesResource} from '~/redux/balances/balancesResource';
import {BalanceSchema} from '~/redux/balances/balancesTypes';
import {selectDynamicsService} from '~/redux/dynamicRate/dynamicRateSelectors';
import {selectFinancesResource} from '~/redux/finances/financesResource';
import {getAllAccounts} from '../../financesPrimitives';
import {getPrivateEquityAccounts} from '../private-equity-accounts';

export const getMoneyResources = (state: AppModel) =>
    Resource.all(
        selectFinancesResource(state).mapSuccess(getAllAccounts),
        selectCurrencyServiceResource(state),
        selectTotalBalanceInAll(state)
    );

export const getInstrumentsResources = (state: AppModel) =>
    Resource.all(
        selectBalancesResource(state).mapSuccess(getSummableBalances),
        selectCurrencyServiceResource(state),
        selectTotalBalanceInAll(state),
        selectDynamicsService(state)
    );

interface Summable {
    dynamics?: DoubleString;
    balance: DoubleString;
    currencyCode: BaseCurrencyCode;
}

const getSummableBalances = (balances: BalanceSchema[]) =>
    balances.map(balance => ({
        type: balance.type,
        balance: balance.current_value,
        dynamics: balance.dynamics,
        currencyCode: balance.currency,
    })) satisfies Summable[];

export const aggregateBalances = (accounts: Summable[], cs: CurrencyService) => {
    let balanceSum = BigNumber(0);
    for (const account of accounts) {
        const converted = cs.convertToPrimary(account.balance, account.currencyCode);
        balanceSum = balanceSum.plus(converted);
    }
    return {
        balance: balanceSum,
    };
};

const selectTotalBalanceInAll = (state: AppModel) =>
    Resource.all(
        selectCurrencyServiceResource(state),
        selectFinancesResource(state).mapSuccess(getAllAccounts),
        selectTokenAccountsResource(state).mapSuccess(getPrivateEquityAccounts),
        selectBalancesResource(state).mapSuccess(getSummableBalances)
    ).mapSuccess(([cs, ...groups]) => {
        // this is such a shit code and a vile hack, too bad
        const accounts = groups.flat();
        type AccountTypes = (typeof accounts)[number]['type'];
        const typeToGroupMap = {
            fiat: 'cash',
            crypto: 'crypto',
            stablecoin: 'crypto',
            bond: 'bonds',
            etf: 'funds',
            note: 'notes',
            stock: 'stocks',
            token: 'venture',
        } satisfies {[K in AccountTypes]: string};
        const sums = {} as Record<string, BigNumber>;
        for (const {balance, currencyCode, type} of accounts) {
            const group = typeToGroupMap[type];
            const converted = cs.convertToPrimary(balance, currencyCode);
            sums[group] = (sums[group] ?? (sums[group] = BigNumber(0))).plus(converted);
        }
        const totalInAll = BigNumber.sum(...Object.values(sums).map(sum => BigNumber.max(0, sum)));
        return {
            getPercentOf(group: Summable[]): Percent {
                if (totalInAll.eq(0)) {
                    return Percent.zero;
                }
                let sum = BigNumber(0);
                for (const {balance, currencyCode} of group) {
                    sum = sum.plus(cs.convertToPrimary(balance, currencyCode));
                }
                sum = BigNumber.max(0, sum);
                return Percent(sum.dividedBy(totalInAll).toNumber());
            },
        };
    });
